<script setup>
// TODO Better error handling
import {
  asArray,
  contactLabel,
  contactMailHref,
  contactSmsHref,
  contactTelHref,
  hasMeaningfulString,
  hasValidString,
  internationalPhoneDisplayText,
  isLoginSeasonalPath,
  shouldRenderContactValue,
  smsPhoneDisplayText,
} from '~/utils/footer-contact'
import {
  persistFooterCourseTopicInterest,
} from '~/utils/footer-course-topic'

const footer = ref([]);

const {globalFeaturesData} = useGlobalFeaturesData();
const {data: globalFeatures } = await globalFeaturesData();
footer.value = Array.isArray(globalFeatures.value?.footer)
  ? globalFeatures.value.footer
  : []

const footerRows = computed(() => (Array.isArray(footer.value) ? footer.value : []))

const getContactImageAlt = (contactInfo) =>
  resolveMediaAlt(contactInfo?.phoneImage)
const getSocialIconAlt = (social) =>
  resolveMediaAlt(social?.socialIcon)

const { fetchCompanyConfigurableText } = useRawPageData();
const privacyData = ref(null);
const termsData = ref(null);
const privacyFetchError = ref(false);
const termsFetchError = ref(false);
const privacyLoading = ref(false);
const termsLoading = ref(false);

const filterCourses = async (genre, link) => {
  if (!link) return
  const interest = useInterest();
  const route = useRoute();
  if (genre) interest.update(genre);
  if (import.meta.client) {
    // Preserve topic choice across hard navigation from shell routes.
    persistFooterCourseTopicInterest(window.sessionStorage, genre);
  }
  if (route.path.startsWith('/account') || route.path.startsWith('/register')) {
    // Allow persisted-state plugin one microtask to flush before hard navigation.
    await nextTick();
    window.location.assign(link);
    return;
  }
  navigateTo(link);
}

defineProps({
  footerMenu: {
    type: Boolean,
    default: true
  }
})

const showPrivacyContent = ref(false);
const showTermsContent = ref(false);

const applyCompanyPayload = (payload) => {
  if (payload?.statusCode != null && payload.statusCode !== 200) return null;
  return payload?.message?.[0]?.text ? payload : null;
};

/** Reused show handler; never throws — `finally` always clears loading. */
const createModalShowHandler = (configId, dataRef, showRef, errorRef, loadingRef, logLabel) =>
  async () => {
    errorRef.value = false;
    try {
      if (!dataRef.value) {
        loadingRef.value = true;
        const payload = await fetchCompanyConfigurableText(configId);
        dataRef.value = applyCompanyPayload(payload);
        if (!dataRef.value) errorRef.value = true;
      }
      showRef.value = !!dataRef.value;
    } catch (loadError) {
      errorRef.value = true;
      showRef.value = false;
      if (import.meta.dev) {
        console.warn(`[FooterNavigation] ${logLabel} load failed`, loadError);
      }
    } finally {
      loadingRef.value = false;
    }
  };

let footerModalDetach = null;
let footerModalAttachRetryTimer = null;
/** Stops retry scheduling after unmount (avoids post-teardown setTimeout). */
const footerModalAttachRetryCancelled = ref(false);

function attachFooterModalListeners(modalEl, modalTC) {
  if (!modalEl || !modalTC) {
    if (import.meta.dev) {
      console.warn('[FooterNavigation] Skipping modal listeners: privacy or terms modal node missing');
    }
    return;
  }

  try {
    if (footerModalDetach) footerModalDetach();
  } catch {
    footerModalDetach = null;
  }

  const onPrivacyShow = createModalShowHandler(
    292040,
    privacyData,
    showPrivacyContent,
    privacyFetchError,
    privacyLoading,
    'Privacy policy',
  );
  const onPrivacyHidden = () => {
    showPrivacyContent.value = false;
    privacyFetchError.value = false;
    privacyLoading.value = false;
  };
  const onTermsShow = createModalShowHandler(
    292039,
    termsData,
    showTermsContent,
    termsFetchError,
    termsLoading,
    'Terms',
  );
  const onTermsHidden = () => {
    showTermsContent.value = false;
    termsFetchError.value = false;
    termsLoading.value = false;
  };

  try {
    modalEl.addEventListener('show.bs.modal', onPrivacyShow);
    modalEl.addEventListener('hidden.bs.modal', onPrivacyHidden);
    modalTC.addEventListener('show.bs.modal', onTermsShow);
    modalTC.addEventListener('hidden.bs.modal', onTermsHidden);
  } catch (listenerBindError) {
    if (import.meta.dev) {
      console.warn('[FooterNavigation] Modal listener attach failed', listenerBindError);
    }
    return;
  }

  footerModalDetach = () => {
    try {
      modalEl.removeEventListener('show.bs.modal', onPrivacyShow);
      modalEl.removeEventListener('hidden.bs.modal', onPrivacyHidden);
      modalTC.removeEventListener('show.bs.modal', onTermsShow);
      modalTC.removeEventListener('hidden.bs.modal', onTermsHidden);
    } catch {
      /* ignore */
    }
    footerModalDetach = null;
  };
}

onMounted(() => {
  /** ClientOnly mounts modals after this component; retry until both roots exist. */
  footerModalAttachRetryCancelled.value = false;
  let attempts = 0;
  const maxAttempts = 150;
  const retryMs = 40;

  const tryAttach = () => {
    if (footerModalAttachRetryCancelled.value) return;
    const modalEl = document.getElementById('privacy-policy-footer');
    const modalTC = document.getElementById('terms-conditions-footer');
    if (modalEl && modalTC) {
      try {
        attachFooterModalListeners(modalEl, modalTC);
      } catch (modalAttachError) {
        if (import.meta.dev) {
          console.warn('[FooterNavigation] attachFooterModalListeners failed', modalAttachError);
        }
      }
      return;
    }
    if (footerModalAttachRetryCancelled.value) return;
    attempts += 1;
    if (attempts < maxAttempts && !footerModalAttachRetryCancelled.value) {
      footerModalAttachRetryTimer = setTimeout(tryAttach, retryMs);
    } else {
      console.warn('[FooterNavigation] Footer modals not found; privacy/terms lazy load disabled');
    }
  };

  nextTick(() => tryAttach());
});

onUnmounted(() => {
  footerModalAttachRetryCancelled.value = true;
  if (footerModalAttachRetryTimer) {
    clearTimeout(footerModalAttachRetryTimer);
    footerModalAttachRetryTimer = null;
  }
  if (footerModalDetach) footerModalDetach();
});

</script>

<template>
  <footer id="footer" v-if="footerMenu">
    <div class="container">
      <!-- Footer's head -->
      <div
        class="footer-head"
        v-for="(contactInfo, index) in footerRows?.filter((identifier) => identifier && identifier.component === 'company.contact-info')"
        :key="`contact-head-${contactInfo.id ?? index}`"
      >
        <div class="row text-center text-lg-start">
          <div class="menu-title mb-lg-3">
            <NuxtImg
              v-if="hasMeaningfulString(contactInfo.phoneImage?.url)"
              class="icon-feather-smartphone me-2 mb-3 mb-lg-0"
              :src="contactInfo?.phoneImage?.url"
              :alt="getContactImageAlt(contactInfo)"
              :title="contactInfo?.phoneImage?.caption || undefined"
              aria-hidden="true"
            />
            <div class="d-lg-inline">{{ contactInfo?.menuTitle ?? '' }}</div>
          </div>
          <div class="ms-lg-6">
            <div class="d-lg-flex flow-flex-row justify-content-between">
              <div>
                <div v-if="hasValidString(contactInfo?.daysOfOperation)" class="foot-links">{{ contactInfo?.daysOfOperation }}</div>
                <div v-if="hasValidString(contactInfo?.timesOfOperation)" class="foot-links">{{ contactInfo?.timesOfOperation }}</div>
              </div>
              <div class="foot-links mt-4 mt-lg-0">
                <div v-if="shouldRenderContactValue(contactInfo?.enablePhone, contactInfo?.phone)">
                  <a :href="contactTelHref(contactInfo?.phone)">{{ contactLabel(contactInfo?.phoneText, contactInfo?.phone) }}</a>
                </div>
                <div v-if="shouldRenderContactValue(contactInfo?.enableInternationalPhone, contactInfo?.internationalPhone)">
                  <a :href="contactTelHref(contactInfo?.internationalPhone)">{{ contactLabel(internationalPhoneDisplayText(contactInfo), contactInfo?.internationalPhone) }}</a>
                </div>
              </div>
              <div class="foot-links">
                <div v-if="shouldRenderContactValue(contactInfo?.enableEmail, contactInfo?.email)">
                  <a :href="contactMailHref(contactInfo?.email)">{{ contactLabel(contactInfo?.emailText, contactInfo?.email) }}</a>
                </div>
                <div v-if="shouldRenderContactValue(contactInfo?.enableSmsPhone, contactInfo?.smsPhone)">
                  <a :href="contactSmsHref(contactInfo?.smsPhone)">{{ contactLabel(smsPhoneDisplayText(contactInfo), contactInfo?.smsPhone) }}</a>
                </div>
              </div>
              <div class="foot-links mb-3">
                <a :href="`http://idtechcamps.zohodesk.com/portal`">Frequently Asked Questions</a> 
              </div>
              <!-- Empty div so that justify content between doesn't push last element to the end -->
              <div></div>
              <div></div>                 
            </div>
          </div>
        </div>
      </div> 
      
        <!-- Footer's body -->
      <hr class="d-none d-lg-block">
      <div class="row py-2 ms-lg-4">
        <div id="collapsible-footer" class="collapsible-footer d-lg-flex px-0 justify-content-between">
          <div
            class="card px-3 py-2 p-lg-0"
            v-for="(menuItem, miIdx) in footerRows?.filter((identifier) => identifier && identifier.component === 'footer-menu.menu')"
            :key="`footer-menu-${menuItem?.id ?? miIdx}`"
          >
            <div class="card-header">
                <a class="font-weight-bold d-flex justify-content-between d-lg-none collapsed" 
                    data-bs-toggle="collapse" 
                    :data-bs-target="`#collapse-${menuItem?.id ?? miIdx}`"
                    aria-expanded="false" 
                    :aria-controls="`collapse-${menuItem?.id ?? miIdx}`"
                    role="button">
                    {{ menuItem?.menuTitle ?? '' }}
                    <div class="bi bi-plus"></div>
                </a>
                <div class="font-weight-bold d-none d-lg-flex">{{ menuItem?.menuTitle ?? '' }}</div>
            </div>

            <div :id="`collapse-${menuItem?.id ?? miIdx}`" class="collapse d-lg-flex pb-4 pb-lg-0" data-bs-parent="#collapsible-footer">
              <div class="card-body py-0 d-lg-flex">
                <div class="foot-links col px-0">
                  <template v-for="(data, m1Idx) in asArray(menuItem?.menu1)" :key="`m1-${data?.id ?? m1Idx}`">
                    <template v-if="data && data.genre">
                      <a
                        v-if="hasMeaningfulString(data.secondaryMenuLink) && !isLoginSeasonalPath(data.secondaryMenuLink)"
                        data-testid="footer-course-topic-link"
                        :data-footer-course-genre="data.genre?.typeString ?? ''"
                        @click.prevent="filterCourses(data.genre?.typeString, data.secondaryMenuLink)"
                      >
                        {{ data.secondaryMenuTitle ?? '' }}
                      </a>
                    </template>
                    <template v-else-if="data">
                      <a
                        v-if="hasMeaningfulString(data.secondaryMenuLink) && !isLoginSeasonalPath(data.secondaryMenuLink)"
                        :href="data.secondaryMenuLink"
                      >
                        {{ data.secondaryMenuTitle ?? '' }}
                      </a>
                      <a
                        v-else-if="hasMeaningfulString(data.secondaryMenuLink)"
                        :href="data.secondaryMenuLink"
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                      >
                        {{ data.secondaryMenuTitle ?? '' }}
                      </a>
                    </template>
                  </template>
                </div>
                <div class="foot-links col px-0 menu2" v-if="asArray(menuItem?.menu2).length > 0">
                  <a
                    v-for="(data, m2Idx) in asArray(menuItem?.menu2)"
                    :key="`m2-${data?.id ?? m2Idx}`"
                    :href="hasMeaningfulString(data?.secondaryMenuLink) ? data.secondaryMenuLink : '#'"
                  >
                    {{ data?.secondaryMenuTitle ?? '' }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <!-- Empty div so that justify content between doesn't push last element to the end -->
          <div></div>
        </div>
      </div>
      <!-- Footer's footer -->
      <hr class="d-none d-lg-block">
      <div class="row footer-foot py-2 px-3 px-lg-0 ms-lg-4">
        <div class="d-flex flex-md-row flex-column-reverse flex-lg-row justify-content-between">
          <div class="mt-4 mt-lg-0">
            <div class="foot-links">        
              <NuxtLink data-bs-toggle="modal" data-bs-target="#privacy-policy-footer">Privacy Policy</NuxtLink>
              <NuxtLink data-bs-toggle="modal" data-bs-target="#terms-conditions-footer">Terms & Conditions</NuxtLink>
              <a href="/#manage_cookies">Manage Cookies</a>
            </div>
            <div
              class="copywrite"
              v-for="(contactInfo, ccIdx) in footerRows?.filter((identifier) => identifier && identifier.component === 'company.contact-info')"
              :key="`contact-copy-${contactInfo?.id ?? ccIdx}`"
            >
              <div v-if="hasMeaningfulString(contactInfo?.copyrightContent)" class="foot-links">{{ contactInfo?.copyrightContent }}</div>
            </div>
          </div>
          <div
            v-for="(connectWithUs, cwIdx) in footerRows?.filter((identifier) => identifier && identifier.component === 'footer-menu.connect-with-us')"
            :key="`connect-${connectWithUs?.id ?? cwIdx}`"
          >
            <p v-if="hasMeaningfulString(connectWithUs?.socialTitle)" class="mb-2 foot-links">{{ connectWithUs?.socialTitle }}</p>
            <div>
              <div class="social-links">
                <template v-for="(social, sIdx) in asArray(connectWithUs.socialShare)" :key="`social-${social?.id ?? sIdx}`">
                  <NuxtLink
                    v-if="social && hasMeaningfulString(social?.socialIcon?.url)"
                    :href="hasMeaningfulString(connectWithUs?.socialUrl) ? connectWithUs?.socialUrl : '#'"
                    class="pe-3"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <NuxtImg
                      :src="social?.socialIcon?.url"
                      :alt="getSocialIconAlt(social)"
                      :title="social?.socialIcon?.caption || undefined"
                      aria-hidden="true"
                    />
                  </NuxtLink>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div> 
  </footer>
  <ClientOnly>
    <UIModal modalId="privacy-policy-footer" modalClass="modal-dialog-scrollable" modalTitle="iD Tech Privacy Policy">
      <GenericContent
        v-if="showPrivacyContent && privacyData"
        :data="{ content: privacyData?.message?.[0]?.text || '' }"
      />
      <p v-else-if="privacyLoading" class="mb-0 px-1 small text-muted" aria-live="polite">
        Loading…
      </p>
      <p v-else-if="privacyFetchError" class="text-danger mb-0 px-1 small" role="alert">
        Unable to load this content. Please try again later.
      </p>
    </UIModal>
    <UIModal modalId="terms-conditions-footer" modalClass="modal-dialog-scrollable" modalTitle="iD Tech Terms & Conditions">
      <GenericContent
        v-if="showTermsContent && termsData"
        :data="{ content: termsData?.message?.[0]?.text || '' }"
      />
      <p v-else-if="termsLoading" class="mb-0 px-1 small text-muted" aria-live="polite">
        Loading…
      </p>
      <p v-else-if="termsFetchError" class="text-danger mb-0 px-1 small" role="alert">
        Unable to load this content. Please try again later.
      </p>
    </UIModal>
  </ClientOnly>
</template>

<style lang="scss">
  $font-size-x-small: .75rem;
  $footer-line-color: #707070;

  body.has-floating-footer #footer {
  padding-bottom: 8rem; 
}

  #footer {
    background: black;
    color: white;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    font-weight: normal;
    padding-top: 3rem;
    padding-bottom: 3rem;

    hr {
      background-color: $footer-line-color;
    }

    .icon-feather-smartphone {
      width: 20px;
      height: 28px;
    }

    .menu-title {
      font-weight: $font-weight-bold;
      line-height: 1.63;
    }

    .foot-links {
      font-size: $font-size-x-small;
      line-height: 2;
      letter-spacing: 1.1px;
      a {
        display: block;
        color: $color-light-gray;
        text-decoration: none;
        cursor: pointer;

        &:hover {
          font-weight: 600;
          color: white;
          text-decoration: underline;
        }

        &:focus-visible {
          outline: none;
          background-color: rgba(255, 255, 255, 0.15);
          border-radius: 4px;
        }
      } 
      &.menu2  {
        margin: 0 1rem 0 6rem;

        @include media-breakpoint-down(lg) {
          margin: 0;
        }
      }   
    }

    .collapsible-footer {
      color: black;

      .card {
        background-color: black;
        border-bottom: 1px solid $footer-line-color;
        --bs-card-border-radius: none;

        &:first-child {
          border-top: 1px solid $footer-line-color;
        }

        @include media-breakpoint-up(lg) {
          border: none;
          &:first-child {
            border-top: none;
          }
        }

        .card-header {
          background-color: black;
          border: none;
          color: white;

          a {
            color: white;
            text-decoration: none;

            &:not(.collapsed) {
              .bi-plus {
                transform: rotate(45deg);
              }
            }
          }
        }
      }
    }

    .footer-foot {
      .social-links {
        a img {
          &:hover {
            background-color: #d9d9d9;
            border-radius: 20px;
            filter: grayscale(1) invert(1) brightness(5.0);
          }
        }
      }
    }
  }
</style>